import { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Trash2, ArrowLeft, Mail, LogOut, UserMinus, Crown, AlertTriangle,
} from 'lucide-react';
import Layout from '../components/Layout';
import { Card, PageHeader, Button, Badge, EmptyState, Spinner, ErrorMessage } from '../components/ui';
import { teamsApi } from '../lib/api';
import type { Team, TeamDetail, TeamMember, TeamInvite, TeamRole } from '../types/teams';
import { useAuth } from '../lib/auth';

function roleTone(role: string): 'accent' | 'success' | 'neutral' {
  if (role === 'owner') return 'accent';
  if (role === 'admin') return 'success';
  return 'neutral';
}

export default function Teams() {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create team
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [creating, setCreating] = useState(false);

  // Selected team detail
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TeamDetail | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [inviting, setInviting] = useState(false);
  const [inviteWarning, setInviteWarning] = useState<string | null>(null);

  const selectedTeam = teams.find((t) => t.id === selectedTeamId) || null;
  const myRole: TeamRole | null = selectedTeam?.role ?? null;
  const canManage = myRole === 'owner' || myRole === 'admin';
  const isOwner = myRole === 'owner';

  const loadTeams = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teamsApi.list();
      if (res.success && res.data) {
        setTeams(res.data.teams);
      } else {
        setError(res.message || 'Failed to load teams');
      }
    } catch {
      setError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const loadDetail = useCallback(async (teamId: string, role: TeamRole) => {
    setDetailLoading(true);
    setInviteWarning(null);
    try {
      const detailRes = await teamsApi.get(teamId);
      if (detailRes.success && detailRes.data) {
        setDetail(detailRes.data.team);
        setMembers(detailRes.data.members);
      } else {
        setError(detailRes.message || 'Failed to load team');
      }
      if (role === 'owner' || role === 'admin') {
        const invRes = await teamsApi.listInvites(teamId);
        if (invRes.success && invRes.data) setInvites(invRes.data.invites);
      } else {
        setInvites([]);
      }
    } catch {
      setError('Failed to load team');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openTeam = (team: Team) => {
    setSelectedTeamId(team.id);
    setDetail(null);
    setMembers([]);
    setInvites([]);
    loadDetail(team.id, team.role);
  };

  const closeTeam = () => {
    setSelectedTeamId(null);
    setDetail(null);
    setMembers([]);
    setInvites([]);
    setInviteWarning(null);
    loadTeams();
  };

  const handleCreate = async () => {
    const name = newTeamName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await teamsApi.create(name);
      if (res.success) {
        setShowCreateModal(false);
        setNewTeamName('');
        loadTeams();
      } else {
        setError(res.message || 'Failed to create team');
      }
    } catch {
      setError('Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async () => {
    if (!selectedTeamId) return;
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    setInviteWarning(null);
    try {
      const res = await teamsApi.invite(selectedTeamId, email, inviteRole);
      if (res.success && res.data) {
        if (res.data.invite.emailSent === false) {
          setInviteWarning(
            `The invite for ${email} was created, but the invitation email failed to send. Revoke the invite and re-invite to retry.`
          );
        }
        setInviteEmail('');
        setInviteRole('member');
        const invRes = await teamsApi.listInvites(selectedTeamId);
        if (invRes.success && invRes.data) setInvites(invRes.data.invites);
      } else {
        setError(res.message || 'Failed to send invite');
      }
    } catch {
      setError('Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    if (!selectedTeamId) return;
    try {
      const res = await teamsApi.revokeInvite(selectedTeamId, inviteId);
      if (res.success) {
        setInvites((prev) => prev.filter((i) => i.id !== inviteId));
      } else {
        setError(res.message || 'Failed to revoke invite');
      }
    } catch {
      setError('Failed to revoke invite');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedTeamId || !selectedTeam) return;
    const isSelf = userId === user?.id;
    const message = isSelf
      ? 'Are you sure you want to leave this team?'
      : 'Remove this member from the team?';
    if (!confirm(message)) return;
    try {
      const res = await teamsApi.removeMember(selectedTeamId, userId);
      if (res.success) {
        if (isSelf) {
          closeTeam();
        } else {
          loadDetail(selectedTeamId, selectedTeam.role);
        }
      } else {
        setError(res.message || 'Failed to remove member');
      }
    } catch {
      setError('Failed to remove member');
    }
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeamId || !detail) return;
    if (!confirm(`Delete team "${detail.name}"? This removes all members and shared connections. This cannot be undone.`)) return;
    try {
      const res = await teamsApi.delete(selectedTeamId);
      if (res.success) {
        closeTeam();
      } else {
        setError(res.message || 'Failed to delete team');
      }
    } catch {
      setError('Failed to delete team');
    }
  };

  const memberName = (m: TeamMember) =>
    [m.firstName, m.lastName].filter(Boolean).join(' ') || '—';

  return (
    <Layout>
      <div className="space-y-6">
        {!selectedTeamId ? (
          <PageHeader
            eyebrow="Collaboration"
            title="Teams"
            description="Create teams and share platform connections with your teammates"
            actions={
              <Button icon={Plus} onClick={() => setShowCreateModal(true)}>
                Create Team
              </Button>
            }
          />
        ) : (
          <PageHeader
            eyebrow="Collaboration"
            title={detail?.name || selectedTeam?.name || 'Team'}
            description="Manage members and pending invites"
            actions={
              <>
                <Button variant="ghost" icon={ArrowLeft} onClick={closeTeam}>
                  All Teams
                </Button>
                {!isOwner && user && (
                  <Button variant="ghost" icon={LogOut} onClick={() => handleRemoveMember(user.id)}>
                    Leave Team
                  </Button>
                )}
                {isOwner && (
                  <Button variant="danger" icon={Trash2} onClick={handleDeleteTeam}>
                    Delete Team
                  </Button>
                )}
              </>
            }
          />
        )}

        {error && (
          <div className="bg-danger-soft border border-line text-danger px-4 py-3 rounded-[11px]">
            <ErrorMessage message={error} />
            <button onClick={() => setError(null)} className="ml-2 text-danger font-bold">&times;</button>
          </div>
        )}

        {!selectedTeamId ? (
          loading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : teams.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No teams yet"
              description="Create a team to share vault connections with your teammates."
              action={
                <Button icon={Plus} onClick={() => setShowCreateModal(true)}>
                  Create Your First Team
                </Button>
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teams.map((team) => (
                <Card
                  key={team.id}
                  padded
                  className="cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-card"
                  onClick={() => openTeam(team)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {team.role === 'owner' && <Crown className="w-4 h-4 text-accent-ink flex-shrink-0" />}
                      <h3 className="font-display font-semibold text-ink">{team.name}</h3>
                    </div>
                    <Badge tone={roleTone(team.role)}>{team.role}</Badge>
                  </div>
                  <p className="text-sm text-ink-2 mt-2">
                    {team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}
                  </p>
                  <p className="text-xs text-ink-3 mt-1 font-mono">
                    Created {new Date(team.createdAt).toLocaleDateString()}
                  </p>
                </Card>
              ))}
            </div>
          )
        ) : detailLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <>
            {/* Members */}
            <Card padded>
              <h3 className="font-display text-base font-semibold text-ink">Members</h3>
              <p className="text-[13px] text-ink-2 mt-1">People with access to connections shared with this team.</p>
              <div className="overflow-x-auto mt-4 -mx-5">
                <table className="w-full">
                  <thead>
                    <tr className="bg-surface-2">
                      <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Email</th>
                      <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Name</th>
                      <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Role</th>
                      <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Added</th>
                      <th className="text-right px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => {
                      const isSelf = m.userId === user?.id;
                      const isMemberOwner = m.role === 'owner';
                      const canRemove = !isMemberOwner && (isSelf || canManage);
                      return (
                        <tr key={m.userId} className="border-t border-line-2 hover:bg-surface-2 transition-colors">
                          <td className="px-5 py-3 text-[13px] text-ink whitespace-nowrap">
                            {m.email}
                            {isSelf && <span className="ml-2 text-xs text-ink-3">(you)</span>}
                          </td>
                          <td className="px-5 py-3 text-[13px] text-ink whitespace-nowrap">{memberName(m)}</td>
                          <td className="px-5 py-3 whitespace-nowrap">
                            <Badge tone={roleTone(m.role)}>{m.role}</Badge>
                          </td>
                          <td className="px-5 py-3 text-[13px] text-ink-2 whitespace-nowrap">
                            {new Date(m.addedAt).toLocaleDateString()}
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-right">
                            {canRemove && (
                              <button
                                onClick={() => handleRemoveMember(m.userId)}
                                className="p-1.5 text-ink-3 hover:text-danger rounded transition-colors"
                                title={isSelf ? 'Leave team' : 'Remove member'}
                              >
                                {isSelf ? <LogOut className="w-4 h-4" /> : <UserMinus className="w-4 h-4" />}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Invites (admins/owners only) */}
            {canManage && (
              <Card padded>
                <h3 className="font-display text-base font-semibold text-ink">Invite a Teammate</h3>
                <p className="text-[13px] text-ink-2 mt-1">They will receive an email with a link to join this team.</p>

                <div className="flex flex-wrap items-end gap-3 mt-4">
                  <div className="flex-1 min-w-[220px]">
                    <label className="ds-label">Email</label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleInvite(); }}
                      placeholder="teammate@example.com"
                      className="ds-input"
                    />
                  </div>
                  <div>
                    <label className="ds-label">Role</label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as 'member' | 'admin')}
                      className="ds-input"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <Button icon={Mail} onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
                    {inviting ? 'Sending...' : 'Send Invite'}
                  </Button>
                </div>

                {inviteWarning && (
                  <div className="flex items-start gap-2 bg-warning-soft border border-line text-warning px-4 py-3 rounded-[11px] mt-4 text-sm">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{inviteWarning}</span>
                  </div>
                )}

                {invites.length > 0 && (
                  <div className="overflow-x-auto mt-5 -mx-5">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-surface-2">
                          <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Pending Invite</th>
                          <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Role</th>
                          <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Expires</th>
                          <th className="text-right px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invites.map((inv) => (
                          <tr key={inv.id} className="border-t border-line-2 hover:bg-surface-2 transition-colors">
                            <td className="px-5 py-3 text-[13px] text-ink whitespace-nowrap">{inv.email}</td>
                            <td className="px-5 py-3 whitespace-nowrap">
                              <Badge tone={roleTone(inv.role)}>{inv.role}</Badge>
                            </td>
                            <td className="px-5 py-3 text-[13px] text-ink-2 whitespace-nowrap">
                              {new Date(inv.expiresAt).toLocaleString()}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap text-right">
                              <button
                                onClick={() => handleRevokeInvite(inv.id)}
                                className="p-1.5 text-ink-3 hover:text-danger rounded transition-colors"
                                title="Revoke invite"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}
          </>
        )}

        {/* Create Team Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card ds-card-pad w-full max-w-md shadow-card animate-fade">
              <h2 className="font-display text-lg font-bold text-ink mb-4">Create Team</h2>
              <div>
                <label className="ds-label">Team Name</label>
                <input
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                  placeholder="Marketing Team"
                  autoFocus
                  className="ds-input"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={creating || !newTeamName.trim()}>
                  {creating ? 'Creating...' : 'Create Team'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
